import React, { useState, useEffect, useRef } from 'react';
import { Flag, Eye, Heart, Edit2, Check, X } from 'lucide-react';
import { FaLock } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getUserMVV, 
  saveUserMVV, 
  updateUserMVVSection, 
  deleteUserValue, 
  addUserValue, 
  updateUserValue 
} from '../services/firebase/missionVisionValues';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StartSidebar from '../components/StartSidebar';
import DataDebugger from '../components/DataDebugger';
import PortalBackground from '../components/PortalBackground';

const ExistingDirection = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Initial example content
  const initialContent = {
    mission: "To tangibly improve the lives of our clients and employees.",
    vision: "To grow a legacy of quality, teamwork, and innovation that reshapes the remodeling experience.",
    values: [
      { title: "Customer Focus", description: "We put our customers at the center of everything we do." },
      { title: "Innovation", description: "We constantly seek new ways to improve and evolve." },
      { title: "Integrity", description: "We operate with honesty, transparency, and ethical standards." },
      { title: "Excellence", description: "We strive for the highest quality in all our endeavors." }
    ]
  };

  // State for storing content
  const [content, setContent] = useState(initialContent);
  
  // State for tracking edit mode
  const [editMode, setEditMode] = useState({
    mission: false,
    vision: false,
    values: Array(initialContent.values.length).fill(false)
  });

  // State for storing original content when editing starts
  const [originalContent, setOriginalContent] = useState({
    mission: '',
    vision: '',
    values: []
  });

  // State for AI assistance
  const [aiAssistMode, setAiAssistMode] = useState({
    mission: false,
    vision: false,
    valueTitle: false,
    valueDescription: false
  });

  const [aiInput, setAiInput] = useState({
    mission: "",
    vision: "",
    valueTitle: "",
    valueDescription: ""
  });

  // State for loading indicators
  const [isLoading, setIsLoading] = useState({
    mission: false,
    vision: false,
    valueTitle: false,
    valueDescription: false
  });

  // State for initial data loading
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // State for notifications
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // Save MVV data to businessAssessment and hrData collections
  const saveMVVToBusinessAssessment = async () => {
    if (!currentUser) return;
    
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        // Save to businessAssessment (existing structure)
        'businessAssessment.mission': content.mission,
        'businessAssessment.vision': content.vision,
        'businessAssessment.values': content.values,
        'businessAssessment.mvvCompleted': true,
        'businessAssessmentProgress.mvvCompleted': true,
        
        // Also save to hrData collection for debugging and separate access
        'hrData.mission': content.mission,
        'hrData.vision': content.vision,
        'hrData.values': content.values,
        'hrData.mvvCompleted': true,
        
        lastUpdated: new Date().toISOString()
      });
      
      // Copy MVV data to lead document if user has a lead account
      try {
        console.log('Copying MVV data to lead document...');
        const mvvData = {
          mission: content.mission,
          vision: content.vision,
          values: content.values,
          mvvCompleted: true
        };
        
        const progressData = {
          mvvCompleted: true
        };
        
        // const success = await copyBusinessAssessmentToLead(
        //   currentUser.email,
        //   mvvData,
        //   progressData
        // );
        
        // if (success) {
        //   console.log('Successfully copied MVV data to lead document');
        // } else {
        //   console.log('No lead account found or failed to copy MVV data');
        // }
      } catch (leadCopyError) {
        console.error('Error copying MVV data to lead:', leadCopyError);
        // Continue even if lead copy fails
      }
      
    } catch (error) {
      console.error('Error saving MVV to business assessment:', error);
      throw error;
    }
  };

  // Load user data from Firebase on component mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) {
        setIsInitialLoading(false);
        return;
      }

      try {
        setIsInitialLoading(true);
        const userData = await getUserMVV(currentUser.uid);
        setContent(userData);
        // Store the original content for cancel functionality
        setOriginalContent({
          mission: userData.mission || '',
          vision: userData.vision || '',
          values: userData.values || []
        });
      } catch (error) {
        console.error('Error loading user MVV data:', error);
        // Fall back to initial content if there's an error
        setContent(initialContent);
        setOriginalContent({
          mission: initialContent.mission,
          vision: initialContent.vision,
          values: initialContent.values
        });
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  // Sync editMode.values array length with content.values length
  useEffect(() => {
    if (content && content.values && Array.isArray(content.values)) {
      const currentValuesLength = editMode.values.length;
      const newValuesLength = content.values.length;
      
      if (currentValuesLength !== newValuesLength) {
        setEditMode(prev => ({
          ...prev,
          values: Array(newValuesLength).fill(false)
        }));
      }
    }
  }, [content, editMode.values.length]);

  // Handle text changes (only update local state, don't save to Firebase yet)
  const handleTextChange = (section, text) => {
    const updatedContent = {
      ...content,
      [section]: text
    };
    
    setContent(updatedContent);
  };

  // Save changes to Firebase
  const saveChanges = async (section) => {
    if (!currentUser) return;
    
    try {
      await updateUserMVVSection(currentUser.uid, section, content[section]);
      // Exit edit mode after successful save
      setEditMode({
        ...editMode,
        [section]: false
      });
    } catch (error) {
      console.error('Error saving to Firebase:', error);
      showNotification('Failed to save changes. Please try again.', 'error');
    }
  };

  // Toggle edit mode for a section
  const toggleEditMode = (section) => {
    const newEditMode = !editMode[section];
    
    if (newEditMode) {
      // Store original content when entering edit mode
      setOriginalContent(prev => ({
        ...prev,
        [section]: content[section]
      }));
    }
    
    setEditMode({
      ...editMode,
      [section]: newEditMode
    });
  };

  // Toggle AI assist mode
  const toggleAiAssistMode = (section) => {
    setAiAssistMode({
      ...aiAssistMode,
      [section]: !aiAssistMode[section]
    });
    
    if (!aiAssistMode[section]) {
      setAiInput({
        ...aiInput,
        [section]: ""
      });
    }
  };

  // Handle AI input change
  const handleAiInputChange = (section, text) => {
    setAiInput({
      ...aiInput,
      [section]: text
    });
  };

  // Generate AI content
  const generateAiContent = async (section) => {
    setIsLoading({
      ...isLoading,
      [section]: true
    });
    
    try {
      // Simulate API call
      const simulateApiCall = () => {
        return new Promise(resolve => {
          setTimeout(() => {
            let result = "";
            if (section === 'mission') {
              result = `To transform homes and lives through exceptional remodeling services, delivering innovative solutions and outstanding craftsmanship that exceed expectations.`;
            } else if (section === 'vision') {
              result = `To be the most trusted and innovative remodeling company in the region, setting new standards for quality, customer satisfaction, and sustainable home transformations.`;
            }
            resolve(result);
          }, 1500);
        });
      };
      
      const result = await simulateApiCall();
      
      if (section === 'mission') {
        const updatedContent = {
          ...content,
          mission: result
        };
        setContent(updatedContent);
        
                 // Save to Firebase
         if (currentUser) {
           try {
             await updateUserMVVSection(currentUser.uid, 'mission', result);
           } catch (error) {
             console.error('Error saving mission to Firebase:', error);
             showNotification('Failed to save mission. Please try again.', 'error');
           }
         }
      } else if (section === 'vision') {
        const updatedContent = {
          ...content,
          vision: result
        };
        setContent(updatedContent);
        
                 // Save to Firebase
         if (currentUser) {
           try {
             await updateUserMVVSection(currentUser.uid, 'vision', result);
           } catch (error) {
             console.error('Error saving vision to Firebase:', error);
             showNotification('Failed to save vision. Please try again.', 'error');
           }
         }
      }
      
      setAiAssistMode({
        ...aiAssistMode,
        [section]: false
      });
      
      setAiInput({
        ...aiInput,
        [section]: ""
      });
    } catch (error) {
      console.error("Error generating AI content:", error);
    } finally {
      setIsLoading({
        ...isLoading,
        [section]: false
      });
    }
  };

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative">
      <StartSidebar />
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header with Title */}
        <div className="text-center mb-6">
                      <h1 className={`text-3xl font-extrabold tracking-tight sm:text-4xl text-gray-900 dark:text-white`}>
              Company Direction
            </h1>
        </div>

        <div className="text-center mb-12">
          <p className={`max-w-2xl mx-auto text-xl ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
            Define the purpose, direction, and principles that guide your organization
          </p>
        </div>

        {/* Notification */}
        {notification.show && (
          <div className={`mb-6 p-4 rounded-md ${
            notification.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L10.586 10l-1.293 1.293a1 1 0 101.414 1.414L12 11.414l1.293 1.293a1 1 0 001.414-1.414L13.414 10l1.293-1.293a1 1 0 00-1.414-1.414L12 8.586l-1.293-1.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{notification.message}</p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                    className={`inline-flex rounded-md p-1.5 ${
                      notification.type === 'success' 
                        ? 'bg-green-50 text-green-500 hover:bg-green-100' 
                        : 'bg-red-50 text-red-500 hover:bg-red-100'
                    }`}
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {!currentUser ? (
          <div className="text-center py-20">
            <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Please log in to view and edit your mission, vision, and values.</p>
          </div>
        ) : isInitialLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading your mission, vision, and values...</p>
            </div>
          </div>
        ) : (
          <div className="mt-10 space-y-10">
            <div id="mvv-content">
              {/* Mission Section */}
              <div className={`overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex-1 text-center">
                      <h2 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Mission</h2>
                    </div>
                                         <div className="flex space-x-2">
                       <button
                         disabled
                         className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-800 cursor-not-allowed opacity-60"
                       >
                         <FaLock className="mr-1 text-xs" />
                         Use AI
                       </button>
                       {editMode.mission ? (
                         <>
                           <button
                             onClick={() => saveChanges('mission')}
                             className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                           >
                             Save
                           </button>
                           <button
                             onClick={() => {
                               // Reset to original content and exit edit mode
                               setContent({
                                 ...content,
                                 mission: originalContent.mission
                               });
                               setEditMode({
                                 ...editMode,
                                 mission: false
                               });
                             }}
                             className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                           >
                             Cancel
                           </button>
                         </>
                       ) : (
                         <button
                           onClick={() => toggleEditMode('mission')}
                           className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-transparent text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                         >
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                           </svg>
                         </button>
                       )}
                     </div>
                  </div>
                  
                  {aiAssistMode.mission ? (
                    <div className="space-y-3">
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Describe your company's purpose and goals</p>
                      <textarea
                        className={`shadow-sm focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm rounded-md p-2 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        rows="3"
                        value={aiInput.mission}
                        onChange={(e) => handleAiInputChange('mission', e.target.value)}
                        placeholder="e.g., provide a seamless experience for customers and ensure our employees feel taken care of..."
                      />
                      <button
                        onClick={() => generateAiContent('mission')}
                        disabled={isLoading.mission || !aiInput.mission.trim()}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${isLoading.mission || !aiInput.mission.trim() ? 'bg-purple-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'}`}
                      >
                        {isLoading.mission ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                          </>
                        ) : (
                          'Generate Mission'
                        )}
                      </button>
                    </div>
                  ) : editMode.mission ? (
                    <textarea
                      className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded-md p-2 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      rows="3"
                      value={content.mission}
                      onChange={(e) => handleTextChange('mission', e.target.value)}
                    />
                  ) : (
                    <div className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{content.mission}</div>
                  )}
                </div>
              </div>

              {/* Vision Section */}
              <div className={`overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm mt-8`}>
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex-1 text-center">
                      <h2 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Vision</h2>
                    </div>
                                         <div className="flex space-x-2">
                       <button
                         disabled
                         className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-800 cursor-not-allowed opacity-60"
                       >
                         <FaLock className="mr-1 text-xs" />
                         Use AI
                       </button>
                       {editMode.vision ? (
                         <>
                           <button
                             onClick={() => saveChanges('vision')}
                             className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                           >
                             Save
                           </button>
                           <button
                             onClick={() => {
                               // Reset to original content and exit edit mode
                               setContent({
                                 ...content,
                                 vision: originalContent.vision
                               });
                               setEditMode({
                                 ...editMode,
                                 vision: false
                               });
                             }}
                             className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                           >
                             Cancel
                           </button>
                         </>
                       ) : (
                         <button
                           onClick={() => toggleEditMode('vision')}
                           className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-transparent text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                         >
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                           </svg>
                         </button>
                       )}
                     </div>
                  </div>
                  
                  {aiAssistMode.vision ? (
                    <div className="space-y-3">
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Describe your company's aspirations and future goals</p>
                      <textarea
                        className={`shadow-sm focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm rounded-md p-2 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        rows="3"
                        value={aiInput.vision}
                        onChange={(e) => handleAiInputChange('vision', e.target.value)}
                        placeholder="e.g., become the most trusted and innovative remodeling company in the region..."
                      />
                      <button
                        onClick={() => generateAiContent('vision')}
                        disabled={isLoading.vision || !aiInput.vision.trim()}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${isLoading.vision || !aiInput.vision.trim() ? 'bg-purple-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'}`}
                      >
                        {isLoading.vision ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                          </>
                        ) : (
                          'Generate Vision'
                        )}
                      </button>
                    </div>
                  ) : editMode.vision ? (
                    <textarea
                      className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded-md p-2 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      rows="3"
                      value={content.vision}
                      onChange={(e) => handleTextChange('vision', e.target.value)}
                    />
                  ) : (
                    <div className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{content.vision}</div>
                  )}
                </div>
              </div>

              {/* Values Section */}
              <div className={`overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm mt-8`}>
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex-1 text-center">
                      <h2 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Values</h2>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        disabled
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-800 cursor-not-allowed opacity-60"
                      >
                        <FaLock className="mr-1 text-xs" />
                        Use AI
                      </button>
                      <button
                        onClick={() => {
                          const newValue = { title: '', description: '' };
                          const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                          const updatedValues = [...currentValues, newValue];
                          
                          setContent({
                            ...content,
                            values: updatedValues
                          });
                          setEditMode({
                            ...editMode,
                            values: [...editMode.values, true]
                          });
                        }}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-transparent text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {aiAssistMode.values && (
                    <div className="mb-6 space-y-3 border-b pb-6">
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>What characteristics are important to your company?</p>
                      <textarea
                        className={`shadow-sm focus:ring-purple-500 focus:border-purple-500 block w-full sm:text-sm rounded-md p-2 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        rows="3"
                        value={aiInput.values || ''}
                        onChange={(e) => handleAiInputChange('values', e.target.value)}
                        placeholder="e.g., Standing behind our work no matter what..."
                      />
                      <button
                        onClick={async () => {
                          setIsLoading({
                            ...isLoading,
                            values: true
                          });
                          
                          try {
                            // Simulate AI generation
                            await new Promise(resolve => setTimeout(resolve, 1500));
                            const input = aiInput.values.toLowerCase();
                            
                            // Generate a concise title based on the input
                            let title = '';
                            if (input.includes('work') || input.includes('quality')) {
                              title = 'Excellence';
                            } else if (input.includes('customer') || input.includes('client')) {
                              title = 'Customer Focus';
                            } else if (input.includes('honest') || input.includes('trust')) {
                              title = 'Integrity';
                            } else if (input.includes('innovate') || input.includes('new')) {
                              title = 'Innovation';
                            } else if (input.includes('team') || input.includes('together')) {
                              title = 'Collaboration';
                            } else {
                              // Default to a capitalized version of the first few words
                              title = input.split(' ').slice(0, 2).map(word => 
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ');
                            }
                            
                            // Generate a concise description
                            const description = `We embody ${title.toLowerCase()} by ${input}, ensuring exceptional remodeling experiences for our clients.`;
                            
                            const newValue = { title, description };
                            const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                            const updatedValues = [...currentValues, newValue];
                            
                            // Just add to local state, don't save to Firebase yet
                            setContent({
                              ...content,
                              values: updatedValues
                            });
                            
                            // Enter edit mode for the new AI-generated value
                            setEditMode({
                              ...editMode,
                              values: [...editMode.values, true]
                            });
                            
                            setAiAssistMode({
                              ...aiAssistMode,
                              values: false
                            });
                            setAiInput({
                              ...aiInput,
                              values: ''
                            });
                          } catch (error) {
                            console.error('Error generating value:', error);
                          } finally {
                            setIsLoading({
                              ...isLoading,
                              values: false
                            });
                          }
                        }}
                        disabled={isLoading.values || !aiInput.values?.trim()}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                          isLoading.values || !aiInput.values?.trim()
                            ? 'bg-purple-300 cursor-not-allowed'
                            : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                        }`}
                      >
                        {isLoading.values ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                          </>
                        ) : (
                          'Generate Value'
                        )}
                      </button>
                    </div>
                  )}

                  <div className="space-y-6">
                    {content.values && Array.isArray(content.values) && content.values.map((value, index) => (
                      <div key={index} className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            {editMode.values[index] ? (
                              <div className="space-y-3">
                                                                 <input
                                   type="text"
                                   value={value.title}
                                   onChange={(e) => {
                                     const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                                     const newValues = [...currentValues];
                                     newValues[index] = {
                                       ...newValues[index],
                                       title: e.target.value
                                     };
                                     setContent({
                                       ...content,
                                       values: newValues
                                     });
                                   }}
                                  className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded-md ${
                                    darkMode 
                                      ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' 
                                      : 'border-gray-300 text-gray-900 placeholder-gray-500'
                                  }`}
                                  placeholder="Value title..."
                                />
                                                                 <textarea
                                   value={value.description}
                                   onChange={(e) => {
                                     const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                                     const newValues = [...currentValues];
                                     newValues[index] = {
                                       ...newValues[index],
                                       description: e.target.value
                                     };
                                     setContent({
                                       ...content,
                                       values: newValues
                                     });
                                   }}
                                  rows="2"
                                  className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded-md ${
                                    darkMode 
                                      ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' 
                                      : 'border-gray-300 text-gray-900 placeholder-gray-500'
                                  }`}
                                  placeholder="Value description..."
                                />
                              </div>
                            ) : (
                              <div>
                                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value.title}</h3>
                                <p className={`mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{value.description}</p>
                              </div>
                            )}
                          </div>
                                                     <div className="ml-4 flex-shrink-0 flex space-x-2">
                             {editMode.values[index] ? (
                               <>
                                 <button
                                   onClick={async () => {
                                     if (!currentUser) {
                                       showNotification('Please log in to save values.', 'error');
                                       return;
                                     }

                                     try {
                                       const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                                       await updateUserValue(currentUser.uid, index, currentValues[index]);
                                       
                                       // Exit edit mode after successful save
                                       const newEditMode = [...editMode.values];
                                       newEditMode[index] = false;
                                       setEditMode({
                                         ...editMode,
                                         values: newEditMode
                                       });
                                       
                                     } catch (error) {
                                       console.error('Error updating value in Firebase:', error);
                                       showNotification(`Failed to save value: ${error.message}`, 'error');
                                     }
                                   }}
                                   className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                 >
                                   Save
                                 </button>
                                 <button
                                   onClick={() => {
                                     const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                                     
                                     // Check if this is a new value (empty title and description)
                                     const currentValue = currentValues[index];
                                     const isNewValue = !currentValue.title && !currentValue.description;
                                     
                                     if (isNewValue) {
                                       // Remove the new value completely
                                       const newValues = currentValues.filter((_, i) => i !== index);
                                       const newEditMode = editMode.values.filter((_, i) => i !== index);
                                       
                                       setContent({
                                         ...content,
                                         values: newValues
                                       });
                                       setEditMode({
                                         ...editMode,
                                         values: newEditMode
                                       });
                                     } else {
                                       // Reset to original content and exit edit mode
                                       const newValues = [...currentValues];
                                       newValues[index] = originalContent.values[index] || { title: '', description: '' };
                                       setContent({
                                         ...content,
                                         values: newValues
                                       });
                                       const newEditMode = [...editMode.values];
                                       newEditMode[index] = false;
                                       setEditMode({
                                         ...editMode,
                                         values: newEditMode
                                       });
                                     }
                                   }}
                                   className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                 >
                                   Cancel
                                 </button>
                                 <button
                                   onClick={async () => {
                                     if (!currentUser) {
                                       showNotification('Please log in to delete values.', 'error');
                                       return;
                                     }

                                     try {
                                       // Delete from Firebase first
                                       await deleteUserValue(currentUser.uid, index);
                                       
                                       // Only update local state if Firebase delete succeeds
                                       const currentValues = content.values && Array.isArray(content.values) ? content.values : [];
                                       const newValues = currentValues.filter((_, i) => i !== index);
                                       const newEditMode = editMode.values.filter((_, i) => i !== index);
                                       
                                       setContent({
                                         ...content,
                                         values: newValues
                                       });
                                       setEditMode({
                                         ...editMode,
                                         values: newEditMode
                                       });
                                       
                                     } catch (error) {
                                       console.error('Error deleting value from Firebase:', error);
                                       showNotification(`Failed to delete value: ${error.message}`, 'error');
                                     }
                                   }}
                                   className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                 >
                                   Delete
                                 </button>
                               </>
                             ) : (
                               <button
                                 onClick={() => {
                                   // Store original content when entering edit mode
                                   setOriginalContent(prev => ({
                                     ...prev,
                                     values: [...content.values]
                                   }));
                                   
                                   // Enter edit mode
                                   const newEditMode = [...editMode.values];
                                   newEditMode[index] = true;
                                   setEditMode({
                                     ...editMode,
                                     values: newEditMode
                                   });
                                 }}
                                 className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-transparent text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                               >
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                 </svg>
                               </button>
                             )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {content.values && Array.isArray(content.values) && content.values.length === 0 && (
                      <div className={`text-center py-6 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No values defined yet. Click "Add Value" or "Use AI" to get started.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Red Continue Button */}
            <div className="flex justify-center mt-12">
              <button
                onClick={async () => {
                  try {
                    // Save MVV data to businessAssessment collection
                    await saveMVVToBusinessAssessment();
                    showNotification('Mission, Vision, and Values saved successfully!', 'success');
                    
                    // Navigate back to existing conditions
                    navigate('/start/existing-conditions', { state: { fromExistingDirection: true } });
                  } catch (error) {
                    console.error('Error saving MVV data:', error);
                    showNotification('Failed to save data. Please try again.', 'error');
                  }
                }}
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              >
                Continue
                <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

          </div>
        )}
        
        {/* HR Data Debugging */}
        <DataDebugger 
          fieldName="mission" 
          value={content.mission} 
          collection="users" 
          subCollection="hrData" 
        />
        <DataDebugger 
          fieldName="vision" 
          value={content.vision} 
          collection="users" 
          subCollection="hrData" 
        />
        <DataDebugger 
          fieldName="values" 
          value={content.values} 
          collection="users" 
          subCollection="hrData" 
        />
        <DataDebugger 
          fieldName="mvvCompleted" 
          value={true} 
          collection="users" 
          subCollection="hrData" 
        />
      </div>
      </div>
    </div>
  );
};

export default ExistingDirection;